/**
 * t271u: an ad-hoc meeting room's people across the top, under the header: avatar + first name (opens the person), as many
 * as fit on one line up to a cap (8 on a computer, 4 on a phone), then "View all (N)" / "+N" for the Members panel or
 * sheet. You come last. Follows the conversation's members, so it updates when people are added or removed.
 */
import { useEffect, useRef, useState } from 'react';
import type { ConversationMember } from '@tradeoutlet/shared';
import { Avatar } from '@/components/ui';
import { PersonButton } from '@/features/people/PersonDrawer';
import { useMembers } from '@/lib/hooks';

export const STRIP_CAP = { desktop: 8, phone: 4 } as const;
/** Room for one person (avatar, gap and a truncated first name) and for the View all button, in px. */
export const CHIP_W = { desktop: 112, phone: 76 } as const;
const MORE_W = { desktop: 96, phone: 44 } as const;

/** First names; a last initial is added where two people share a first name. */
export function shortNames(people: { user_id: string; display_name: string }[]): Map<string, string> {
  const first = (n: string) => n.trim().split(/\s+/)[0] || n;
  const count = new Map<string, number>();
  people.forEach(p => count.set(first(p.display_name), (count.get(first(p.display_name)) ?? 0) + 1));
  return new Map(people.map(p => {
    const parts = p.display_name.trim().split(/\s+/), f = first(p.display_name);
    return [p.user_id, (count.get(f) ?? 0) > 1 && parts.length > 1 ? `${f} ${parts[parts.length - 1]![0]}.` : f];
  }));
}

/** Who shows: others by name, you last; capped by the layout and by how many fit in `width` (unknown = the cap). */
export function stripPeople<T extends { user_id: string; display_name: string }>(members: T[], me: string | null | undefined, layout: 'desktop' | 'phone', width?: number) {
  const ordered = [...members].sort((a, b) => Number(a.user_id === me) - Number(b.user_id === me) || a.display_name.localeCompare(b.display_name));
  const fitAll = width == null ? Infinity : Math.floor(width / CHIP_W[layout]);
  if (ordered.length <= Math.min(STRIP_CAP[layout], fitAll)) return { shown: ordered, more: 0 };
  const fit = width == null ? Infinity : Math.floor((width - MORE_W[layout]) / CHIP_W[layout]);
  const n = Math.max(1, Math.min(STRIP_CAP[layout], fit));
  return { shown: ordered.slice(0, n), more: ordered.length - n };
}

export function RoomMembersStrip({ members, me, phone, onViewAll }: { members: ConversationMember[]; me: string | null | undefined; phone: boolean; onViewAll: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const { byId } = useMembers();
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => { const w = Math.round(e!.contentRect.width); setWidth(w > 0 ? w : undefined); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  if (!members.length) return null;
  const layout = phone ? 'phone' : 'desktop';
  const { shown, more } = stripPeople(members, me, layout, width);
  const names = shortNames(members);
  const total = members.length;
  return (
    <div className="shrink-0 border-b border-slate-200 px-3 py-1.5 sm:px-5 dark:border-slate-800">
      <div ref={ref} className="flex h-7 flex-nowrap items-center gap-1 overflow-hidden" aria-label="People in this ad-hoc meeting room" data-testid="room-members-strip">
        {shown.map(m => (
          <PersonButton key={m.user_id} userId={m.user_id} name={m.display_name} testId="room-member"
            className="flex min-w-0 shrink-0 items-center gap-1.5 rounded-full py-0.5 pl-0.5 pr-2 text-xs text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800">
            <Avatar size={22} name={m.display_name} id={m.user_id} url={byId.get(m.user_id)?.avatar_url} />
            <span className={phone ? 'max-w-[2.75rem] truncate' : 'max-w-[4.75rem] truncate'}>{m.user_id === me ? 'You' : names.get(m.user_id)}</span>
          </PersonButton>
        ))}
        {more > 0 && (
          <button type="button" onClick={onViewAll} data-testid="room-members-view-all" aria-label={`View all ${total} people`}
            className="ml-auto shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800">
            {phone ? `+${more}` : `View all (${total})`}
          </button>
        )}
      </div>
    </div>
  );
}
